/**
 * Country Connections chord diagram contract.
 * Geometry ratios, ids, and interaction constants — no magic strings in chart code.
 */

import type {
  ChordFlywayFilter,
  ChordViewMode,
} from '@/content/data/charts/chord.types';

/**
 * Ordered flyway filter ids for Country Connections chrome.
 * Index aligns with `intro.chordGraph.filters` message labels (Show ALL first).
 * Excludes `marine` — chord chrome has only these five controls.
 */
export const CHORD_FLYWAY_FILTERS = [
  'all',
  'americas',
  'african-eurasian',
  'central-asia',
  'east-asia-australasia',
] as const satisfies readonly ChordFlywayFilter[];

export type ChordFlywayFilterId = (typeof CHORD_FLYWAY_FILTERS)[number];

/** Breeding / non-breeding view modes for Country Connections chrome + layout. */
export const CHORD_VIEW_MODES = [
  'breeding',
  'nonBreeding',
] as const satisfies readonly ChordViewMode[];

export type ChordViewModeId = (typeof CHORD_VIEW_MODES)[number];

/** Flyway accent colours for compact carousel (active non-`all` filter). */
export const CHORD_FLYWAY_COLORS = {
  americas: 'var(--flyway-americas)',
  'african-eurasian': 'var(--flyway-african-eurasian)',
  'central-asia': 'var(--flyway-central-asia)',
  'east-asia-australasia': 'var(--flyway-east-asia-australasia)',
} as const satisfies Record<Exclude<ChordFlywayFilterId, 'all'>, string>;

/** Outer SVG wrapper id (aria labelledby / testing). */
export const CHORD_GRAPH_SVG_ID = 'country-connections-chord';

/** Reserved plate aspect ratio — circular chart, square plate. */
export const CHORD_GRAPH_ASPECT_RATIO = 1;

/**
 * IntersectionObserver `rootMargin` for the sticky first mount of the Pixi
 * island. Positive margin starts the dynamic import slightly before the
 * plate enters the viewport (scroll audit 2026-07-15). Mount stays sticky —
 * do not unmount on leave (hero clip-path flips IO and tore down WebGL).
 */
export const CHORD_GRAPH_VIEWPORT_ROOT_MARGIN = '200px 0px';

/**
 * Cap on Pixi `resolution` (device pixel ratio). Audit showed DPR 2 (~1077²
 * canvas) + Lenis janked scroll; resolution 1 restored ~96+ FPS. Cap at 2 so
 * Retina matches device DPR without overshooting on higher-DPR displays.
 */
export const CHORD_GRAPH_MAX_RESOLUTION = 2;

/**
 * Trailing debounce (ms) for ParentSize → stage/SVG/mesh rebuild on plate
 * resize. Mesh triangulation is expensive (Show ALL ~8k ribbons); wait until
 * the window settles rather than rebuilding on every ResizeObserver tick.
 */
export const CHORD_GRAPH_RESIZE_DEBOUNCE_MS = 100;

/**
 * Static Show ALL chord snapshot shown until the first Pixi mesh paint.
 * Cropped tight to the outer ring so layout can inset by `CHORD_LABEL_BAND_PX`
 * (same framing as the live SVG/Pixi stage) at every breakpoint.
 * Sourced from `docs/design-refs/chord-geographic-order/chord-show-all-desktop.png`.
 */
export const CHORD_GRAPH_PLACEHOLDER_SRC =
  '/media/chord-graph-placeholder-1012.png';

/** Intrinsic width of `CHORD_GRAPH_PLACEHOLDER_SRC` (srcset descriptor). */
export const CHORD_GRAPH_PLACEHOLDER_WIDTH = 1012;

/** Pad angle between adjacent country arcs (radians) — preferred when n is small. */
export const CHORD_PAD_ANGLE = 0.04;

/**
 * Cap on total pad around the ring as a fraction of 2π. With hundreds of
 * countries the preferred pad would exceed the circle and starve ribbons of
 * angular width (Show ALL looked like an empty ring). Kept high enough that
 * Show ALL still shows a clear cream gap between segments (Figma / Flourish).
 */
export const CHORD_PAD_ANGLE_MAX_FRACTION = 0.2;

/** Pad angle for a layout with `countryCount` groups. */
export function chordPadAngle(countryCount: number): number {
  if (countryCount <= 1) return 0;
  const maxTotal = Math.PI * 2 * CHORD_PAD_ANGLE_MAX_FRACTION;
  return Math.min(CHORD_PAD_ANGLE, maxTotal / countryCount);
}

/**
 * Rotates d3-chord angles so group 0 starts at 9 o'clock (left) and proceeds
 * clockwise. d3’s 0 is 12 o'clock in our polar mapping; −π/2 shifts to 9 o'clock.
 */
export const CHORD_START_ANGLE_OFFSET = -Math.PI / 2;

/**
 * Outer band (px) from the SVG edge to the outer arc, reserved for country
 * labels. Replaces a separate margin ratio + viewBox pad so the ring can fill
 * more of the plate without double-shrinking.
 */
export const CHORD_LABEL_BAND_PX = 52;

/** Outer arc outer-radius = layout radius * this (typically 1). */
export const CHORD_ARC_OUTER_RATIO = 1;

/** Outer arc inner-radius as a fraction of layout radius (ring thickness). */
export const CHORD_ARC_INNER_RATIO = 0.95;

/**
 * Opaque arc paint extends this many px inward past the geometric inner
 * radius so the AA fringe of the segment covers any cream hairline at the
 * flush ribbon join (ribbons meet at the geometric inner edge underneath).
 * Keep small — larger values make segments look thicker than ribbon tips.
 */
export const CHORD_ARC_PAINT_OVERHANG_PX = 1.5;

/**
 * Angular inset on ribbon tips so they sit inside their subgroup and do not
 * AA-bleed into the pad gaps between country segments.
 */
export const CHORD_RIBBON_ANGLE_INSET = 0.002;

/**
 * Cap inset as a fraction of subgroup width so tiny connections are not
 * collapsed to a point by the preferred inset alone.
 */
export const CHORD_RIBBON_ANGLE_INSET_MAX_FRACTION = 0.25;

/** Default country-arc stroke width (px) — reserved if strokes return. */
export const CHORD_ARC_DEFAULT_STROKE_WIDTH = 1.5;

/** Selected country-arc stroke width (px) — reserved if strokes return. */
export const CHORD_ARC_SELECTED_STROKE_WIDTH = 2.5;

/**
 * Ribbon target (draw tip) end radius — inset so a clear gap remains
 * under the destination arc colour block.
 */
export const CHORD_RIBBON_TARGET_RADIUS_RATIO = 0.87;

/** Default ribbon fill opacity (overlaps read as a dense centre). */
export const CHORD_RIBBON_OPACITY = 0.48;

/** Highlighted / selected ribbon opacity (slight bump over default). */
export const CHORD_RIBBON_HIGHLIGHT_OPACITY = 0.82;

/**
 * Non-active ribbons while hovering (no selection). Applied on the dim *layer*
 * (flat composite). Selection hides the dim layer entirely — a soft ghost of
 * ~8k Show-ALL ribbons still reads as a mesh even at very low opacity.
 */
export const CHORD_RIBBON_DIM_OPACITY = 0.01125;

/**
 * Value-label radius as a fraction of the ribbon attach radius at that end
 * (slightly inside the ribbon tip for Flourish-style end numbers).
 */
export const CHORD_VALUE_LABEL_RADIUS_RATIO = 0.92;

/** Centre idle / hover text block max width as fraction of layout radius. */
export const CHORD_CENTRE_TEXT_WIDTH_RATIO = 1.1;

/**
 * Show outer country labels for every segment when the visible country count
 * is below this threshold (flyway filters with few countries).
 */
export const CHORD_OUTER_LABEL_SEGMENT_THRESHOLD = 12;

/**
 * Clear air (px) from the outer arc edge to the centre of each label.
 * Tangential labels share this radius so spacing is equal around the ring.
 * Must stay below `CHORD_LABEL_BAND_PX` so labels fit inside the plate.
 */
export const CHORD_OUTER_LABEL_OFFSET_PX = 22;

/** Soft-wrap outer labels longer than this many characters (prefer word breaks). */
export const CHORD_OUTER_LABEL_WRAP_CHARS = 14;

/** How many top connections to expose in the visually-hidden summary. */
export const CHORD_SUMMARY_TOP_COUNT = 5;

/** How many countries to list in the “Most connected” legend. */
export const CHORD_MOST_CONNECTED_COUNT = 5;

/**
 * Country callout discs (Russia / South Sudan) — viewport entrance, map-badge
 * style. Scale start matches `flywayMapMotion.badgeScaleFrom`; stagger so the
 * second disc starts after the first has begun (1 then the other).
 */
export const CHORD_STAT_DISC_SCALE_FROM = 0.2;
/** Seconds between staggered disc reveals. */
export const CHORD_STAT_DISC_STAGGER_S = 0.2;
/** Scale-in duration (seconds) — map badge fade + scale extras ≈ 0.48s. */
export const CHORD_STAT_DISC_DURATION_S = 0.48;

/** Pixi Application / hit-layer host id (outside-click contract with selection). */
export const CHORD_GRAPH_STAGE_ID = 'country-connections-chord-stage';

/** Samples along each ribbon tip arc when building GPU mesh polygons. */
export const CHORD_MESH_TIP_SAMPLES = 4;

/** Samples along each ribbon quadratic (tip → centre control → tip). */
export const CHORD_MESH_CURVE_SAMPLES = 10;

/** Samples along each country annular-sector edge (inner/outer). */
export const CHORD_MESH_ARC_SAMPLES = 8;

/** Minimum tip width in CSS px so sub-pixel connections stay visible. */
export const CHORD_RIBBON_MIN_TIP_PX = 2;

/**
 * Country Connections ring colour encoding (chart data, not theme tokens).
 * Hue runs red → purple clockwise; stops before magenta/pink so the purple
 * end does not visually close into the red start.
 */
export const CHORD_RING_COLOR_SATURATION = 80;
export const CHORD_RING_COLOR_LIGHTNESS = 54;
/** Hue degrees at ring index 0 (9 o'clock start) — pure red. */
export const CHORD_RING_HUE_START = 0;
/** Hue degrees at the last ring index — violet/purple (not magenta/pink). */
export const CHORD_RING_HUE_END = 270;
